import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, Download, ExternalLink, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { MfaSetup } from '../components/security/MfaSetup';
import { AuditLog } from '../components/security/AuditLog';
import { DpaCard } from '../components/security/DpaCard';
import { AdminCompliance } from '../components/security/AdminCompliance';

const Card = ({ children, testid }) => <section data-testid={testid} className="glass rounded-2xl border border-border p-5 sm:p-6">{children}</section>;

export default function SecurityPage() {
  const { api, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [rk, setRk] = useState(0);
  const bump = () => setRk((k) => k + 1);
  const load = () => { bump(); return api.get('/security/2fa/status').then((r) => setStatus(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportData = async () => {
    try {
      const r = await api.get('/security/export', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = 'mis_datos_notaryos.json'; a.click();
      toast.success('Exportación descargada');
      bump();
    } catch { toast.error('No se pudo exportar'); }
  };

  return (
    <div className="min-h-screen mesh-gradient">
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" data-testid="security-back-link" className="flex items-center gap-2 text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /> Panel</Link>
          <span className="flex items-center gap-2 font-semibold font-['Outfit']"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Seguridad y Cumplimiento</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] tracking-tight">Tu cuenta, blindada.</h1>
          <p className="text-slate-400 mt-3 max-w-2xl text-sm sm:text-base">Doble factor de acceso, registro de auditoría y contrato de tratamiento de datos: lo que exigen los grandes estudios antes de contratar.</p>
        </div>
        {status?.setup_required && (
          <div className="p-4 rounded-xl border border-rose-500/40 bg-rose-500/10 flex gap-3 text-sm" data-testid="mfa-required-alert">
            <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0" /> Como administrador debes activar el doble factor para seguir usando NotaryOS.
          </div>
        )}
        <Card testid="security-mfa-section"><h2 className="text-lg font-semibold mb-4">Doble factor de acceso</h2><MfaSetup status={status} onChange={load} /></Card>
        <Card testid="security-dpa-section"><DpaCard onChange={bump} /></Card>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card testid="security-audit-section"><AuditLog refreshKey={rk} /></Card>
          <Card testid="security-data-section">
            <h2 className="text-sm font-semibold mb-2">Tus datos</h2>
            <p className="text-sm text-slate-400 mb-4">Descarga una copia completa de tus clientes, casos, contratos y registros (derecho de acceso y portabilidad).</p>
            <button onClick={exportData} data-testid="security-export-btn" className="text-sm px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 flex items-center gap-2"><Download className="w-4 h-4" /> Exportar mis datos</button>
            <Link to="/confianza" data-testid="security-trust-link" className="mt-4 text-sm text-blue-400 hover:underline flex items-center gap-1">Ver Centro de Confianza <ExternalLink className="w-3.5 h-3.5" /></Link>
          </Card>
        </div>
        {user?.is_admin && (
          <Card testid="security-admin-section">
            <h2 className="text-lg font-semibold mb-4">Panel de cumplimiento</h2>
            <AdminCompliance />
            <div className="mt-6"><AuditLog scope="all" refreshKey={rk} /></div>
          </Card>
        )}
      </main>
    </div>
  );
}
